'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Menu, X } from 'lucide-react';
import SearchBar from '../common/SearchBar';
import LanguageSelector from '../common/LanguageSelector';
import type { Game } from '@/types';
import type { LanguageCode } from '@/lib/games';

interface HeaderProps {
  currentGame?: Game;
  lang: LanguageCode;
}

const Header = ({ currentGame, lang }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = {
    home: {
      en: 'Miside',
      ja: 'Miside',
      es: 'Miside',
      ko: 'Miside',
      th: 'Miside',
      ru: 'Miside',
      vi: 'Miside',
      kk: 'Miside',
      id: 'Miside',
      de: 'Miside'
    },
    ballPool: {
      en: 'Mita Miside',
      ja: 'Mita Miside',
      es: 'Mita Miside',
      ko: 'Mita Miside',
      th: 'Mita Miside',
      ru: 'Mita Miside',
      vi: 'Mita Miside',
      kk: 'Mita Miside',
      id: 'Mita Miside',
      de: 'Mita Miside'
    },
    puzzleMaster: {
      en: 'Miside Mod',
      ja: 'Miside モッド',
      es: 'Miside Mod',
      ko: '미사이드 모드',
      th: 'Miside มอด',
      ru: 'Miside Мод',
      vi: 'Miside Mod',
      kk: 'Miside Мод',
      id: 'Miside Mod',
      de: 'Miside Mod'
    },
    stacking: {
      en: 'Download Miside',
      ja: 'Misideをダウンロード',
      es: 'Descargar Miside',
      ko: 'Miside 다운로드',
      th: 'ดาวน์โหลด Miside',
      ru: 'Скачать Miside',
      vi: 'Tải Miside',
      kk: 'Miside жүктеу',
      id: 'Unduh Miside',
      de: 'Miside Herunterladen'
    },
    wooden: {
      en: 'Miside Game',
      ja: 'Misideゲーム',
      es: 'Juego Miside',
      ko: 'Miside 게임',
      th: 'เกม Miside',
      ru: 'Игра Miside',
      vi: 'Trò chơi Miside',
      kk: 'Miside ойыны',
      id: 'Game Miside',
      de: 'Miside Spiel'
    }
  };

  const getGameLink = (gameId: string) => {
    return lang === 'en' ? `/${gameId}` : `/${lang}/${gameId}`;
  };

  const getHomeLink = () => {
    return lang === 'en' ? '/' : `/${lang}`;
  };

  const navigation = [
    { name: navLinks.home[lang], href: getHomeLink() },
    { name: navLinks.ballPool[lang], href: getGameLink('mita-miside') },
    { name: navLinks.puzzleMaster[lang], href: getGameLink('miside-mod') },
    { name: navLinks.stacking[lang], href: getGameLink('download-miside') },
    { name: navLinks.wooden[lang], href: getGameLink('miside-game') },
  ];

  return (
    <header className="header-container">
      <nav className="nav-container">
        <div className="nav-left">
          <Link href={getHomeLink()} className="logo-link">
            <Image
              src="/images/games/miside.jpeg"
              alt="Miside"
              width={48}
              height={48}
              className="logo-image"
            />
            <span className="logo-text">{navLinks.home[lang]}</span>
          </Link>

          <div className="desktop-nav">
            {navigation.slice(1).map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="nav-link"
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="nav-right">
          <LanguageSelector />
          <SearchBar />
          <button
            className="mobile-menu-button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="menu-icon" />
            ) : (
              <Menu className="menu-icon" />
            )}
          </button>
        </div>
      </nav>

      {isMobileMenuOpen && (
        <div className="mobile-menu">
          <div className="mobile-menu-links">
            {navigation.slice(1).map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="mobile-nav-link"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      {currentGame && (
        <div className="game-header">
          <h1 className="game-title gradient-text">
            {currentGame.title}
          </h1>
          <p className="game-slogan">
            {currentGame.slogan}
          </p>
        </div>
      )}
    </header>
  );
};

export default Header;